import React, { useMemo } from 'react';
import { Card, Typography, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { SalesTier } from '../../services/DashboardService';
import { BaselineData, BaselineChanges } from '../../services/baselineService';
import { formatPercent } from '../../utils/formatters';
import { LocationCard } from './LocationCard';
import { AverageCard } from './AverageCard';
import { BaselineCard } from './BaselineCard';
import { TierTable } from './TierTable';
import { HighSpendersTable } from './HighSpendersTable';
import { exportDMReport } from '../../utils/dmReportExport';
import { AnomalyFlag } from '../../utils/anomalyDetection';
import { GlobalSortState } from '../../constants/sortableColumns';

const { Title, Text } = Typography;

interface TierCardProps {
  tier: SalesTier;
  sortBy: string;
  isMobile: boolean;
  viewMode: 'weekly' | 'quarterly';
  dataStatus: any;
  columns: any[];
  baseline: BaselineData | null;
  baselineDateRange: string;
  baselineChanges: BaselineChanges | null;
  baselineLoading: boolean;
  baselineChangeLoading: boolean;
  // Props for DM export
  allTiers?: SalesTier[];
  anomalyFlags?: Map<string, AnomalyFlag>;
  baselineDataByTier?: Record<string, BaselineData>;
  dateRangeLabel?: string;
  startDate?: string;
  endDate?: string;
  // Global sort props
  globalSort?: GlobalSortState;
  onGlobalSortChange?: (sort: GlobalSortState) => void;
  // Filter function to exclude new locations from averages
  filterNewLocations?: (location: import('../../services/DashboardService').LocationData) => boolean;
  // Dashboard end date (end of 8-week range) for finding the correct week
  dashboardEndDate?: string;
}

export const TierCard: React.FC<TierCardProps> = ({
  tier,
  sortBy,
  isMobile,
  viewMode,
  dataStatus,
  columns,
  baseline,
  baselineDateRange,
  baselineChanges,
  baselineLoading,
  baselineChangeLoading,
  allTiers,
  anomalyFlags,
  baselineDataByTier,
  dateRangeLabel,
  startDate,
  endDate,
  globalSort,
  onGlobalSortChange,
  filterNewLocations,
  dashboardEndDate,
}) => {
  const handleExportDMReport = () => {
    if (!allTiers || !anomalyFlags || !baselineDataByTier || !dateRangeLabel) {
      return;
    }
    
    exportDMReport(
      tier.tierName,
      tier.locations,
      allTiers,
      anomalyFlags,
      baselineDataByTier,
      dateRangeLabel,
      startDate,
      endDate
    );
  };
  const { locationsWithEightWeekStats, avgEightWeekPercent, filteredAvgGfsPercent, filteredLocationsForAverage } = useMemo(() => {
    // Filter out new locations if filter function is provided
    // This filter should exclude locations that haven't appeared in at least 8 complete 8-week ranges
    // IMPORTANT: This uses the same filtering logic as the baseline (filterLocationsByEightWeekRanges)
    const locationsForAvg = filterNewLocations 
      ? tier.locations.filter(filterNewLocations)
      : tier.locations;

    // Also filter the locations that will be used for calculations (for TierTable average row)
    const filteredLocationsForTable = filterNewLocations
      ? tier.locations.filter(filterNewLocations)
      : tier.locations;

    // Calculate tier average GFS% excluding new locations
    const filteredAvgGfs = locationsForAvg.length > 0
      ? locationsForAvg.reduce((sum, loc) => sum + loc.gfsPercent, 0) / locationsForAvg.length
      : tier.avgGfsPercent; // Fallback to original if no locations after filtering

    // Use filtered locations for percent calculations
    const percentValues = filteredLocationsForTable.map((location) => {
      if (
        !location.eightWeekTotalSales ||
        location.eightWeekTotalSales <= 0 ||
        location.eightWeekTotalGfs === null ||
        location.eightWeekTotalGfs === undefined
      ) {
        return null;
      }
      return (location.eightWeekTotalGfs / location.eightWeekTotalSales) * 100;
    });

    // Calculate 8-week average excluding new locations
    const valid8WeekPercents = percentValues
      .filter((value): value is number => value !== null && !Number.isNaN(value));
    
    const avgPercent =
      valid8WeekPercents.length > 0
        ? valid8WeekPercents.reduce((sum, value) => sum + value, 0) / valid8WeekPercents.length
        : null;

    // Create enhanced locations for all original locations (for display in table)
    const enhancedAllLocations = tier.locations.map((location) => {
      // Find this location in filtered list to get its stats
      const filteredIdx = filteredLocationsForTable.indexOf(location);
      const percent = filteredIdx >= 0 ? percentValues[filteredIdx] : null;
      return {
        ...location,
        eightWeekGfsPercentValue: percent,
        eightWeekGfsVariance: percent !== null && avgPercent !== null ? percent - avgPercent : null,
      };
    });

    // Create filtered locations for average calculation
    // Use location name for comparison to avoid object reference issues
    const filteredLocationNames = new Set(filteredLocationsForTable.map(loc => loc.location.toLowerCase().trim()));
    const filteredLocationsForAverage = enhancedAllLocations.filter((location) => 
      filteredLocationNames.has(location.location.toLowerCase().trim())
    );

    return {
      // Return all locations for display, but pass filtered locations separately for average calculation
      locationsWithEightWeekStats: enhancedAllLocations,
      avgEightWeekPercent: avgPercent ?? null,
      filteredAvgGfsPercent: filteredAvgGfs,
      // Store filtered locations for average row calculation
      filteredLocationsForAverage,
    };
  }, [tier.locations, tier.avgGfsPercent, filterNewLocations]);

  return (
    <div data-tour="tier-table">
    <Card
      key={tier.tierName}
      title={
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-start', paddingTop: 4, gap: isMobile ? 12 : 0 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {sortBy === 'dm' ? `${tier.tierName} (DM)` : tier.tierName}
            </Title>
            <Text type="secondary">
              {tier.locationCount} locations{tier.tierRange ? `, ${tier.tierRange}` : ''}, Avg GFS%:{' '}
              {formatPercent(filteredAvgGfsPercent)}
            </Text>
          </div>
          {sortBy === 'dm' && allTiers && anomalyFlags && baselineDataByTier && dateRangeLabel && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportDMReport}
              size="small"
              style={{ marginTop: isMobile ? 0 : 4 }}
              block={isMobile}
            >
              Export DM Report
            </Button>
          )}
        </div>
      }
      style={{ marginBottom: 24 }}
    >
      {isMobile ? (
        <div>
          {/* High Spenders Alert Table - shown FIRST above location data */}
          {viewMode === 'weekly' && (
            <HighSpendersTable
              locations={locationsWithEightWeekStats}
              baseline={baseline}
              tierName={tier.tierName}
              dashboardEndDate={dashboardEndDate}
            />
          )}
          {locationsWithEightWeekStats.map((location, idx) => (
            <div key={`${tier.tierName}-${idx}`}>
              <LocationCard
                location={location}
                isDMView={sortBy === 'dm'}
                viewMode={viewMode}
                dataStatus={dataStatus}
                startDate={startDate}
                endDate={endDate}
              />
            </div>
          ))}
          <AverageCard
            locations={filterNewLocations
              ? locationsWithEightWeekStats.filter(filterNewLocations)
              : locationsWithEightWeekStats
            }
            viewMode={viewMode}
          />
          {(baseline || baselineLoading) && (
            <BaselineCard
              baseline={baseline}
              baselineDateRange={baselineDateRange}
              baselineChanges={baselineChanges}
              loading={baselineLoading}
              changeLoading={baselineChangeLoading}
            />
          )}
        </div>
      ) : (
        <>
          {/* High Spenders Alert Table - shown FIRST above main data table */}
          {viewMode === 'weekly' && (
            <HighSpendersTable
              locations={locationsWithEightWeekStats}
              baseline={baseline}
              tierName={tier.tierName}
              dashboardEndDate={dashboardEndDate}
            />
          )}
          <TierTable
            tier={tier}
            locations={locationsWithEightWeekStats}
            filteredLocationsForAverage={filteredLocationsForAverage}
            columns={columns}
            isQuarterly={viewMode === 'quarterly'}
            baseline={baseline}
            baselineDateRange={baselineDateRange}
            baselineChanges={baselineChanges}
            baselineLoading={baselineLoading}
            baselineChangeLoading={baselineChangeLoading}
            avgEightWeekPercent={avgEightWeekPercent}
            globalSort={globalSort}
            onGlobalSortChange={onGlobalSortChange}
          />
        </>
      )}
    </Card>
    </div>
  );
};
